"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.splitAmountByPercents = exports.splitRoundingAmount = void 0;
const types_1 = require("../types");
const general_util_1 = require("./general.util");
const price_util_1 = require("./price.util");
/**
 * Splits a given amount across a list of items, adjusting their total field values
 * to account for rounding differences.
 *
 * @template T - The type of the items in the list.
 * @param {number} amount - The total amount to be split among the items.
 * @param {T[]} items - The list of items to split the amount across.
 * @param {string} [totalField=PriceFieldEnum.TOTAL_PRICE] - The field in each item that represents the total price.
 */
function splitRoundingAmount(amount, items, totalField = types_1.PriceFieldEnum.TOTAL_PRICE) {
    if (!(0, price_util_1.validateAmount)(amount) || !items?.length) {
        return;
    }
    const amountItems = items.map(item => ({ amount: item[totalField] }));
    const roundingSplit = calculateRoundingSplit(amountItems, amount);
    const itemsSplit = [];
    for (let startIndex = 0, splitNumber = 0; splitNumber < roundingSplit.splitsNumber; splitNumber++) {
        startIndex = splitRoundingAmountToProduct(amount, items, roundingSplit, itemsSplit, startIndex, totalField);
    }
    for (const index in items) {
        items[index][totalField] = (0, price_util_1.roundPriceCents)(items[index][totalField] + (itemsSplit[index] ?? 0));
    }
}
exports.splitRoundingAmount = splitRoundingAmount;
/**
 * Splits a total amount into individual amounts specified by the given percent values.
 * @param {number} amount - The total amount to be split.
 * @param {number[]} percents - The list of percent values (must sum to 100).
 * @returns {number[]} The split amounts that correspond to the given percent values.
 */
function splitAmountByPercents(amount, percents) {
    const percentSum = percents.reduce((sum, amount) => sum + amount, 0);
    if (percentSum != 100) {
        throw new Error('Percent values must sum to 100');
    }
    const splits = percents.map(percent => ({ amount: (0, price_util_1.percentOfAmount)(amount, percent) }));
    splitRoundingAmount(amount, splits, types_1.PriceFieldEnum.AMOUNT);
    return splits.map(split => split.amount);
}
exports.splitAmountByPercents = splitAmountByPercents;
/**
 * Split the rounding amount to a specific product.
 * @param {number} amount - The requested total amount
 * @param {T[]} items - The items to split the rounding amount to.
 * @param {IRoundingSplit} roundingSplit - The rounding split details.
 * @param {number[]} amountSplit - The array to store the split amounts.
 * @param {number} startIndex - The starting index for splitting.
 * @param {PriceFieldEnum} [totalField=PriceFieldEnum.TOTAL_PRICE] - The field to use for total price.
 * @returns {number} The next starting index.
 */
function splitRoundingAmountToProduct(amount, items, roundingSplit, amountSplit, startIndex, totalField = types_1.PriceFieldEnum.TOTAL_PRICE) {
    let index = findSplit(amount, items, roundingSplit, amountSplit, startIndex, totalField);
    if (!(0, price_util_1.validateAmount)(index)) {
        throw new Error('Failed to split rounding amount to resources');
    }
    amountSplit[index] = (0, price_util_1.roundPriceCents)((amountSplit?.[index] ?? 0) + roundingSplit.splitAmount);
    return (index + 1) % items.length;
}
/**
 * Find the index to split the rounding amount to.
 * @param {number} amount - The requested total amount
 * @param items {T[]} The items to split the rounding amount to.
 * @param roundingSplit {IRoundingSplit} The rounding split details.
 * @param resourcesSplit {number[]} The array to store the split amounts.
 * @param startIndex {number} The starting index for splitting.
 * @param totalField {PriceFieldEnum} The field to use for total price.
 * @returns {number} The index to split the rounding amount to.
 */
function findSplit(amount, items, roundingSplit, resourcesSplit, startIndex, totalField = types_1.PriceFieldEnum.TOTAL_PRICE) {
    let foundIndex = null;
    let index = startIndex;
    do {
        const item = items[index];
        const maxSplitNumber = Math.ceil((item[totalField] / amount) * roundingSplit.splitsNumber);
        const maxSplitAmount = (0, price_util_1.roundPriceCents)(maxSplitNumber * roundingSplit.splitAmount);
        const isValid = (0, general_util_1.absoluteValue)(resourcesSplit?.[index]) < (0, general_util_1.absoluteValue)(maxSplitAmount);
        if (isValid) {
            const totalAfterSplit = (0, price_util_1.roundPriceCents)(item[totalField] + (resourcesSplit?.[index] ?? 0) + roundingSplit.splitAmount);
            if ((0, price_util_1.validatePrice)(totalAfterSplit)) {
                foundIndex = index;
            }
        }
        index = (index + 1) % items.length;
    } while (!(0, price_util_1.validateAmount)(foundIndex) && index !== startIndex);
    return foundIndex;
}
function calculateRoundingSplit(amountItems, amount) {
    let roundingSplit = { splitsNumber: 0, splitAmount: 0 };
    if (amount) {
        const currentAmount = (0, price_util_1.calculateTotalPriceByPriceField)(amountItems, 0, types_1.PriceFieldEnum.AMOUNT);
        roundingSplit = (0, price_util_1.getRoundingSplit)(amount, currentAmount);
    }
    return roundingSplit;
}
//# sourceMappingURL=split-amount.util.js.map